import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Icon, QrCode, buttonClasses, cn } from '@forecastify/ui';
import {
  CONTROL_PANEL_PATHS,
  PASSWORD_RULES,
  checkPassword,
  operatorEnrolmentSchema,
  operatorJoinPreviewSchema,
  passwordAcceptable,
  type OperatorEnrolment,
  type OperatorJoinPreview,
  type PasswordRule,
} from '@forecastify/shared';
import { apiCall } from '../api';
import { useT, type MessageKey } from '../i18n';
import { useOperatorSession } from '../session';
import { SIGN_IN_PATH } from '../routes';
import {
  AuthCard,
  AuthInput,
  AuthLede,
  AuthSteps,
  CODE_INPUT,
  TextInput,
} from '../chrome/auth-card';
import { useToast } from '../chrome/toasts';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ `0179` · Joining from an invitation: Jelszó · Kulcs · Ellenőrzés.         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE CODE IS WHAT FINISHES SIGNING UP, AND IT ALSO SIGNS THEM IN.** The invitation used to
 * be a product password reset, after which the invitee was expected to find `/enrol` by themselves
 * — and an operator with no verified factor cannot sign in at all, so most stopped there. Here the
 * password, the key and the first code are one flow, and the last step answers with a session: the
 * app then moves a signed-in operator off this public screen into the console, with a toast.
 *
 * ⚠️ **The password is held in memory between steps**, as `/enrol` holds it: GoTrue needs a fresh
 * sign-in to verify the factor against, and there is no session to carry instead.
 *
 * ⚠️ **The drawing has no such screen.** It reuses the enrolment card's frame and step bar, which
 * the mockup does draw, so it reads as the same flow somebody would meet on `/enrol`.
 */

type Step = 0 | 1 | 2;

const STEP_LABEL: readonly MessageKey[] = [
  'join.step.password',
  'enrol.step.key',
  'enrol.step.verify',
];

const RULE_LABEL: Readonly<Record<PasswordRule['id'], MessageKey>> = {
  length: 'join.rule.length',
  letters: 'join.rule.letters',
  digits: 'join.rule.digits',
  match: 'join.rule.match',
};

type Preview =
  | { readonly state: 'loading' }
  | { readonly state: 'dead' }
  | { readonly state: 'ready'; readonly invitee: OperatorJoinPreview };

export function JoinScreen(): ReactNode {
  const t = useT();
  const session = useOperatorSession();
  const toast = useToast();
  const { token = '' } = useParams<{ token: string }>();

  const [preview, setPreview] = useState<Preview>({ state: 'loading' });
  const [step, setStep] = useState<Step>(0);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [code, setCode] = useState('');
  const [enrolment, setEnrolment] = useState<OperatorEnrolment | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    void apiCall<unknown>(CONTROL_PANEL_PATHS.join(token)).then((result) => {
      if (!alive) return;
      const parsed = result.ok ? operatorJoinPreviewSchema.safeParse(result.value) : null;
      setPreview(
        parsed?.success === true ? { state: 'ready', invitee: parsed.data } : { state: 'dead' },
      );
    });
    return () => {
      alive = false;
    };
  }, [token]);

  const rules = checkPassword(password, confirmation);
  const acceptable = passwordAcceptable(password, confirmation);

  const begin = (): void => {
    if (busy || !acceptable) return;
    setBusy(true);
    setFailure(null);
    void apiCall<unknown>(CONTROL_PANEL_PATHS.joinBegin(token), {
      method: 'POST',
      body: JSON.stringify({ password }),
    }).then((result) => {
      setBusy(false);
      if (!result.ok) {
        if (result.code === 'not_found') setPreview({ state: 'dead' });
        else
          setFailure(
            result.code === 'weak_password'
              ? t('join.weak')
              : result.code === 'unreachable'
                ? t('error.unreachable')
                : t('error.unknown'),
          );
        return;
      }
      const parsed = operatorEnrolmentSchema.safeParse(result.value);
      if (!parsed.success) {
        setFailure(t('error.unknown'));
        return;
      }
      setEnrolment(parsed.data);
      setStep(1);
    });
  };

  const finish = (): void => {
    if (busy || enrolment === null) return;
    if (!/^\d{6}$/u.test(code)) {
      setFailure(t('signIn.codeShape'));
      return;
    }
    setBusy(true);
    setFailure(null);
    void apiCall<unknown>(CONTROL_PANEL_PATHS.joinVerify(token), {
      method: 'POST',
      body: JSON.stringify({ password, factorId: enrolment.factorId, totpCode: code }),
    }).then((result) => {
      if (!result.ok) {
        setBusy(false);
        setCode('');
        if (result.code === 'not_found') setPreview({ state: 'dead' });
        else
          setFailure(result.code === 'unreachable' ? t('error.unreachable') : t('join.wrongCode'));
        return;
      }
      /* ⚠️ The toast before the session: adopting it re-renders the app off this screen. */
      toast({ title: t('join.welcome'), description: t('join.welcomeBody') });
      if (!session.adopt(result.value)) {
        setBusy(false);
        setFailure(t('error.unknown'));
      }
    });
  };

  if (preview.state === 'loading') {
    return (
      <AuthCard screen="join" title={t('join.title')}>
        <p role="status" className="text-muted-foreground text-[13px]">
          {t('join.loading')}
        </p>
      </AuthCard>
    );
  }

  if (preview.state === 'dead') {
    return (
      <AuthCard screen="join" title={t('join.dead.title')}>
        <AuthLede>{t('join.dead.lede')}</AuthLede>
        <Link to={SIGN_IN_PATH} className={buttonClasses('outline', 'lg', 'w-full')}>
          {t('enrol.backToSignIn')}
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard screen="join" title={t('join.title')}>
      <div className="flex flex-col gap-3">
        <AuthSteps
          label={t('enrol.steps')}
          steps={STEP_LABEL.map((key) => t(key))}
          current={step}
        />
      </div>

      {step === 0 ? (
        <form
          className="contents"
          onSubmit={(event) => {
            event.preventDefault();
            begin();
          }}
        >
          <p className="text-muted-foreground text-[12.5px] leading-[1.5] text-pretty">
            {t('join.lede')}
          </p>
          <div className="bg-muted/50 flex flex-col gap-0.5 rounded-md px-3 py-2">
            <span className="text-muted-foreground text-[11.5px]">{t('join.as')}</span>
            <span className="truncate text-[13px] font-medium">{preview.invitee.email}</span>
          </div>
          {/* ⚠️ The address, for the password manager: without a username field beside it, the
              saved password is filed under nothing and offered for the wrong site. */}
          <input
            type="email"
            autoComplete="username"
            value={preview.invitee.email}
            readOnly
            hidden
          />
          <AuthInput label={t('join.password')}>
            <TextInput
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                setFailure(null);
              }}
            />
          </AuthInput>
          <AuthInput label={t('join.confirm')} error={failure}>
            <TextInput
              type="password"
              autoComplete="new-password"
              value={confirmation}
              onChange={(event) => {
                setConfirmation(event.target.value);
                setFailure(null);
              }}
            />
          </AuthInput>
          <ul aria-label={t('join.rules')} className="flex flex-col gap-1">
            {PASSWORD_RULES.map((rule) => (
              <li
                key={rule.id}
                className={cn(
                  'flex items-center gap-1.5 text-[12px]',
                  rules[rule.id] ? 'text-[var(--status-ok-text)]' : 'text-muted-foreground',
                )}
              >
                <Icon name={rules[rule.id] ? 'circle-check' : 'circle'} size={13} />
                {t(RULE_LABEL[rule.id], rule.min === undefined ? undefined : { min: rule.min })}
              </li>
            ))}
          </ul>
          <Button type="submit" size="lg" className="w-full" disabled={busy || !acceptable}>
            {busy ? t('join.beginWorking') : t('join.begin')}
          </Button>
        </form>
      ) : null}

      {step === 1 && enrolment !== null ? (
        <>
          <p className="text-muted-foreground text-[12.5px] leading-[1.55]">
            {t('enrol.scanHint')}
          </p>
          <div className="flex items-start gap-4">
            <div className="shrink-0">
              <QrCode value={enrolment.uri} label={t('enrol.qrLabel')} size={132} />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <span className="text-muted-foreground text-[12px] font-medium">
                {t('enrol.secret')}
              </span>
              <code className="bg-muted rounded-md px-2.5 py-2 font-sans text-[13px] tracking-[.08em] break-all tabular-nums">
                {enrolment.secret.replace(/(.{4})/gu, '$1 ').trim()}
              </code>
              <Button
                variant="outline"
                size="sm"
                className="h-7 self-start px-2.5"
                onClick={() => {
                  void navigator.clipboard.writeText(enrolment.secret).then(() => {
                    setCopied(true);
                  });
                }}
              >
                <Icon name="copy" size={13} />
                {copied ? t('enrol.copied') : t('enrol.copy')}
              </Button>
            </div>
          </div>
          <p className="text-muted-foreground border-t pt-3 text-[12px] leading-[1.5]">
            {t('enrol.secretNote')}
          </p>
          <Button
            size="lg"
            className="w-full"
            onClick={() => {
              setStep(2);
            }}
          >
            {t('signIn.continue')}
          </Button>
        </>
      ) : null}

      {step === 2 ? (
        <form
          className="contents"
          onSubmit={(event) => {
            event.preventDefault();
            finish();
          }}
        >
          <p className="text-muted-foreground text-[12.5px]">{t('enrol.verifyLede')}</p>
          <AuthInput label={t('signIn.code')} error={failure}>
            <TextInput
              className={CODE_INPUT}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={(event) => {
                setCode(event.target.value.replace(/\D/gu, '').slice(0, 6));
                setFailure(null);
              }}
            />
          </AuthInput>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              size="lg"
              onClick={() => {
                setFailure(null);
                setStep(1);
              }}
            >
              {t('signIn.back')}
            </Button>
            <Button type="submit" size="lg" className="flex-1" disabled={busy}>
              {busy ? t('join.finishWorking') : t('join.finish')}
            </Button>
          </div>
        </form>
      ) : null}
    </AuthCard>
  );
}
